import { Component, Inject, OnDestroy, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MeetingSchedulerStoreService } from '../../../data-providers/meeting-scheduler-store.service';
import { SessionAttendeesInterface } from '@vendasta/meetings';
import {
  GalaxyTableModule,
  GalaxyDataSource,
  GalaxyColumnDef,
  PagedListRequestInterface,
  PagedResponseInterface,
  PaginatedAPIInterface,
  TableContainerComponent,
} from '@vendasta/galaxy/table';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule, Router } from '@angular/router';
import { BehaviorSubject, Observable, map, combineLatest, of } from 'rxjs';
import { Row, GalaxyTableSelectionCustomizationToken } from '@vendasta/galaxy/table';
import {
  GalaxyFilterChipsModule,
  GalaxyFilterChipInjectionToken,
  GalaxyFilterInterface,
  GalaxyFilterDefinitionInterface,
} from '@vendasta/galaxy/filter/chips';
import { GalaxyFilterType, GalaxyFilterOperator } from '@vendasta/galaxy/filter/chips';
import { Subscription } from 'rxjs';
import { CONTACT_PROFILE_PAGE_TOKEN } from '../../../data-providers/providers';

export interface AttendeeListDialogData {
  meetingId: string;
  meetingTitle?: string;
}

class AttendeeTableService {
  private dataSource: GalaxyDataSource<SessionAttendeesInterface, GalaxyFilterInterface>;

  constructor(
    private meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private meetingId: string,
  ) {
    const paginatedAPI: PaginatedAPIInterface<SessionAttendeesInterface, GalaxyFilterInterface> = {
      get: (
        req: PagedListRequestInterface<GalaxyFilterInterface>,
      ): Observable<PagedResponseInterface<SessionAttendeesInterface>> => {
        return new Observable((observer) => {
          this.meetingSchedulerStoreService
            .listMeetingAttendees(this.meetingId, {
              cursor: req.pagingOptions?.cursor,
              pageSize: req.pagingOptions?.pageSize || 25,
            })
            .then((response) => {
              let confirmedAttendees = (response.attendees || []).filter((attendee) => attendee.isBookingConfirmed);

              // Apply search filtering
              if (req.searchOptions?.text && req.searchOptions.text.trim()) {
                const searchTerm = req.searchOptions.text.toLowerCase().trim();
                confirmedAttendees = confirmedAttendees.filter((attendee) => {
                  const fullName = `${attendee.firstName} ${attendee.lastName}`.toLowerCase();
                  const email = (attendee.email || '').toLowerCase();
                  const phone = (attendee.phoneNumber || '').toLowerCase();
                  const comment = (attendee.comment || '').toLowerCase();

                  return (
                    fullName.includes(searchTerm) ||
                    email.includes(searchTerm) ||
                    phone.includes(searchTerm) ||
                    comment.includes(searchTerm)
                  );
                });
              }

              // Apply filters
              if (req.filters && req.filters.length > 0) {
                confirmedAttendees = this.applyFilters(confirmedAttendees, req.filters);
              }

              observer.next({
                data: confirmedAttendees,
                pagingMetadata: {
                  nextCursor: response.metadata?.nextCursor || '',
                  hasMore: response.metadata?.hasMore || false,
                  totalResults: confirmedAttendees.length,
                },
              });
              observer.complete();
            })
            .catch((error) => {
              observer.error(error);
            });
        });
      },
    };

    this.dataSource = new GalaxyDataSource<SessionAttendeesInterface, GalaxyFilterInterface>(paginatedAPI);
  }

  getDataSource(): GalaxyDataSource<SessionAttendeesInterface, GalaxyFilterInterface> {
    return this.dataSource;
  }

  async deleteAttendee(attendeeId: string, email: string): Promise<void> {
    await this.meetingSchedulerStoreService.deleteMeetingAttendee(this.meetingId, attendeeId, email);
    // Trigger refetch by setting shouldRefetch to true
    this.dataSource.state = { shouldRefetch: true };
  }

  private applyFilters(
    attendees: SessionAttendeesInterface[],
    filters: GalaxyFilterInterface[],
  ): SessionAttendeesInterface[] {
    return attendees.filter((attendee) => {
      return filters.every((filter) => {
        return this.matchesFilter(attendee, filter);
      });
    });
  }

  private matchesFilter(attendee: SessionAttendeesInterface, filter: GalaxyFilterInterface): boolean {
    const { fieldId, operator, values } = filter;

    if (!values || values.length === 0) {
      return true;
    }

    let fieldValue = '';
    switch (fieldId) {
      case 'name':
        fieldValue = `${attendee.firstName} ${attendee.lastName}`.trim();
        break;
      case 'email':
        fieldValue = attendee.email || '';
        break;
      case 'phone':
        fieldValue = attendee.phoneNumber || '';
        break;
      case 'comment':
        fieldValue = attendee.comment || '';
        break;
      default:
        return true;
    }

    return values.some((value) => {
      const searchValue = (value.string || value.phone?.nationalNumber || '').toLowerCase();
      const fieldValueLower = fieldValue.toLowerCase();

      switch (operator) {
        case GalaxyFilterOperator.FILTER_OPERATOR_CONTAINS:
          return fieldValueLower.includes(searchValue);
        case GalaxyFilterOperator.FILTER_OPERATOR_DOES_NOT_CONTAIN:
          return !fieldValueLower.includes(searchValue);
        case GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY:
          return fieldValue === '';
        case GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY:
          return fieldValue !== '';
        case GalaxyFilterOperator.FILTER_OPERATOR_IS_VALID:
          // For phone validation - simplified check
          return fieldId === 'phone' ? fieldValue.length >= 10 : true;
        case GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_VALID:
          // For phone validation - simplified check
          return fieldId === 'phone' ? fieldValue.length < 10 : false;
        default:
          return true;
      }
    });
  }
}

@Component({
  selector: 'meeting-scheduler-attendee-list',
  templateUrl: './attendee-list.component.html',
  styleUrls: ['./attendee-list.component.scss'],
  standalone: true,
  providers: [
    {
      provide: GalaxyTableSelectionCustomizationToken,
      useValue: { rowsSelectionLimit: 100 },
    },
    {
      provide: GalaxyFilterChipInjectionToken,
      useFactory: () => ({
        listObjectFilters: (_searchTerm: string) => {
          return of([
            {
              fieldId: 'name',
              fieldName: 'Name',
              type: GalaxyFilterType.FILTER_TYPE_STRING,
              supportedOperators: [
                GalaxyFilterOperator.FILTER_OPERATOR_CONTAINS,
                GalaxyFilterOperator.FILTER_OPERATOR_DOES_NOT_CONTAIN,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY,
              ],
            },
            {
              fieldId: 'email',
              fieldName: 'Email',
              type: GalaxyFilterType.FILTER_TYPE_STRING,
              supportedOperators: [
                GalaxyFilterOperator.FILTER_OPERATOR_CONTAINS,
                GalaxyFilterOperator.FILTER_OPERATOR_DOES_NOT_CONTAIN,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY,
              ],
            },
            {
              fieldId: 'phone',
              fieldName: 'Phone',
              type: GalaxyFilterType.FILTER_TYPE_PHONE,
              supportedOperators: [
                GalaxyFilterOperator.FILTER_OPERATOR_CONTAINS,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_EQUAL_TO,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EQUAL_TO,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_VALID,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_VALID,
              ],
            },
            {
              fieldId: 'comment',
              fieldName: 'Comments',
              type: GalaxyFilterType.FILTER_TYPE_STRING,
              supportedOperators: [
                GalaxyFilterOperator.FILTER_OPERATOR_CONTAINS,
                GalaxyFilterOperator.FILTER_OPERATOR_DOES_NOT_CONTAIN,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_EMPTY,
                GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_EMPTY,
              ],
            },
          ] as GalaxyFilterDefinitionInterface[]);
        },
      }),
    },
  ],
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatMenuModule,
    GalaxyTooltipModule,
    GalaxyFormFieldModule,
    GalaxyTableModule,
    GalaxyEmptyStateModule,
    GalaxyFilterChipsModule,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    TranslateModule,
    RouterModule,
  ],
})
export class AttendeeListComponent implements OnDestroy, AfterViewInit {
  @ViewChild('tableContainer', { static: false }) tableContainer?: TableContainerComponent<SessionAttendeesInterface>;

  dataSource: GalaxyDataSource<SessionAttendeesInterface, GalaxyFilterInterface>;
  columns$: Observable<GalaxyColumnDef[]>;
  searchQuery = '';
  selectedAttendees: Row[] = [];
  initialLoadingCompleted$: Observable<boolean>;
  showEmptyState$: Observable<boolean>;
  displayedColumns: string[] = ['select', 'name', 'email', 'phone', 'comment', 'actions'];
  useSelectAll = false;
  contactProilePageLink: string;
  private searchTimeout?: ReturnType<typeof setTimeout>;
  private tableService: AttendeeTableService;
  private appliedFilters: GalaxyFilterInterface[] = [];
  private subscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<AttendeeListComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AttendeeListDialogData,
    private meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private alerts: SnackbarService,
    @Inject(CONTACT_PROFILE_PAGE_TOKEN) public readonly contactProfilePageLink$: Observable<string>,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    this.tableService = new AttendeeTableService(this.meetingSchedulerStoreService, this.data.meetingId);
    this.dataSource = this.tableService.getDataSource();
    this.columns$ = this.createColumns();

    // Initialize observables for template
    this.initialLoadingCompleted$ = this.dataSource.state$.pipe(map((state) => state.initialLoadCompleted === true));

    // Show empty state only when there are no attendees AND no active search
    this.showEmptyState$ = combineLatest([this.dataSource.dataMembers$, this.dataSource.state$]).pipe(
      map(([data, state]) => {
        const hasNoData = data && data.length === 0;
        const hasNoSearch = !state.searchTerm || state.searchTerm.trim() === '';
        return hasNoData && hasNoSearch;
      }),
    );
    this.subscriptions.push(contactProfilePageLink$.subscribe((value) => (this.contactProilePageLink = value)));
  }

  ngAfterViewInit(): void {
    // Update selection limit dynamically based on actual attendee count
    this.subscriptions.push(
      this.dataSource.dataMembers$.subscribe((data) => {
        if (data?.length && this.tableContainer) {
          this.updateSelectionLimit(data.length);
        }
      }),
    );
  }

  private updateSelectionLimit(count: number): void {
    const container = this.tableContainer as any;

    // Update both the selection service and header component
    if (container.selectionService) {
      container.selectionService.rowsSelectionLimit = count;
    }
    if (container.tableContentHeader) {
      container.tableContentHeader.rowsSelectionLimit = count;
    }

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Unsubscribe from all subscriptions to prevent memory leaks
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private createColumns(): Observable<GalaxyColumnDef[]> {
    return new BehaviorSubject<GalaxyColumnDef[]>([
      {
        id: 'select',
        title: '',
        sortable: false,
        hidden: false,
        pinned: true,
      },
      {
        id: 'name',
        title: 'MEETING_SCHEDULER.ATTENDEE_LIST.NAME_LABEL',
        sortable: false,
        hidden: false,
      },
      {
        id: 'email',
        title: 'MEETING_SCHEDULER.ATTENDEE_LIST.EMAIL_LABEL',
        sortable: false,
        hidden: false,
      },
      {
        id: 'phone',
        title: 'MEETING_SCHEDULER.ATTENDEE_LIST.PHONE_LABEL',
        sortable: false,
        hidden: false,
      },
      {
        id: 'comment',
        title: 'MEETING_SCHEDULER.ATTENDEE_LIST.COMMENTS_LABEL',
        sortable: false,
        hidden: false,
      },
      {
        id: 'actions',
        title: '',
        sortable: false,
        hidden: false,
        stickyEnd: true,
      },
    ]).asObservable();
  }

  onSelectionChanged(selectedRows: Row[]): void {
    this.selectedAttendees = selectedRows;
  }

  onRemoveAttendee(attendee: SessionAttendeesInterface): void {
    this.tableService
      .deleteAttendee(attendee.id, attendee.email)
      .then(() => {
        this.alerts.openSuccessSnack('MEETING_SCHEDULER.ATTENDEE_LIST.DELETE_SUCCESS');
        // Refresh the data source to reflect the deletion
        this.dataSource.state = { shouldRefetch: true };
      })
      .catch((error) => {
        console.error('Error removing attendee:', error);
        this.alerts.openErrorSnack('MEETING_SCHEDULER.ATTENDEE_LIST.DELETE_ERROR');
      });
  }

  onDeleteSelected(): void {
    if (this.selectedAttendees.length === 0) return;

    // Delete all selected attendees
    const deletePromises = this.selectedAttendees.map((row) => {
      // Cast row to SessionAttendeesInterface to access the email property
      const attendee = row as unknown as SessionAttendeesInterface;
      return this.tableService.deleteAttendee(attendee.id, attendee.email);
    });

    Promise.all(deletePromises)
      .then(() => {
        this.alerts.openSuccessSnack('MEETING_SCHEDULER.ATTENDEE_LIST.DELETE_SUCCESS');
        // Refresh the data source to reflect the deletion
        this.dataSource.state = { shouldRefetch: true };
        // Clear selection after successful deletion
        this.selectedAttendees = [];
        this.useSelectAll = false;
      })
      .catch((error) => {
        console.error('Error deleting attendees:', error);
        this.alerts.openErrorSnack('MEETING_SCHEDULER.ATTENDEE_LIST.DELETE_ERROR');
      });
  }

  handleSelectAll(): void {
    this.dataSource.toggleSelectAll();
    this.useSelectAll = !this.useSelectAll;
  }

  get totalAttendees(): number {
    return this.dataSource.state.dataMembers?.length || 0;
  }

  onSearchChange(searchTerm?: string): void {
    // Update searchQuery if provided from the event, otherwise use the current value
    if (searchTerm !== undefined) {
      this.searchQuery = searchTerm;
    }

    // The dataSource.setSearchTerm will update the state immediately,
    // which our showEmptyState$ observable will react to
    this.dataSource.setSearchTerm(this.searchQuery);
  }

  onFiltersChanged(filters: GalaxyFilterInterface[]): void {
    this.appliedFilters = filters;
    // Apply filters to the data source properly
    this.dataSource.state = {
      filters: filters,
      shouldRefetch: true,
    };
  }

  onClose(): void {
    // Simple close without any action
    this.dialogRef.close({
      confirmedAttendeesCount: this.dataSource.state.dataMembers?.length || 0,
      deleted: false,
    });
  }

  navigateToContact(attendee: SessionAttendeesInterface): void {
    if (!attendee.contactId) {
      return;
    }

    this.dialogRef.close();
    this.router.navigateByUrl(`${this.contactProilePageLink}${attendee.contactId}`);
  }

  hasContactId(attendee: SessionAttendeesInterface): boolean {
    return !!attendee.contactId;
  }
}
